import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = "of_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const api = {
  getConfig:    () => axios.get(`${API}/site/config`).then((r) => r.data),
  getPricing:   () => axios.get(`${API}/site/pricing`).then((r) => r.data),
  getStats:     () => axios.get(`${API}/site/stats/downloads`, { headers: authHeaders() }).then((r) => r.data),
  trackDownload: () => axios.post(`${API}/site/track-download`).catch(() => {}),
  uploadImage:  (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/site/upload`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  login:        (username, password) =>
    axios.post(`${API}/auth/login`, { username, password }).then((r) => r.data),
  me:           () => axios.get(`${API}/auth/me`, { headers: authHeaders() }).then((r) => r.data),
  saveConfig:   (data) =>
    axios.put(`${API}/site/config`, data, { headers: authHeaders() }).then((r) => r.data),
  addCategory:  (tab, data) =>
    axios.post(`${API}/site/pricing/${tab}`, data, { headers: authHeaders() }).then((r) => r.data),
  updateCategory: (id, data) =>
    axios.put(`${API}/site/pricing/${id}`, data, { headers: authHeaders() }).then((r) => r.data),
  deleteCategory: (id) =>
    axios.delete(`${API}/site/pricing/${id}`, { headers: authHeaders() }).then((r) => r.data),
  reorder:      (tab, ids) =>
    axios.post(`${API}/site/pricing/${tab}/reorder`, { ids }, { headers: authHeaders() }).then((r) => r.data),
  // Gallery
  getGallery:        () => axios.get(`${API}/site/gallery`).then((r) => r.data),
  addGalleryItem:    (data) => axios.post(`${API}/site/gallery`, data, { headers: authHeaders() }).then((r) => r.data),
  updateGalleryItem: (id, data) => axios.put(`${API}/site/gallery/${id}`, data, { headers: authHeaders() }).then((r) => r.data),
  deleteGalleryItem: (id) => axios.delete(`${API}/site/gallery/${id}`, { headers: authHeaders() }).then((r) => r.data),
  reorderGallery:    (ids) => axios.post(`${API}/site/gallery/reorder`, { ids }, { headers: authHeaders() }).then((r) => r.data),
  repairGalleryHeic: () => axios.post(`${API}/site/gallery/repair`, {}, { headers: authHeaders() }).then((r) => r.data),
};

// Helper: zwraca pełny URL do obrazka — dodaje prefix backendu jeśli ścieżka zaczyna się od /api
export const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

// Helper: drukuje wybraną sekcję (cennik | quote) — kod historyczny.
// Cennik drukuje się przez printCennik(), wycena przez printQuote().

/* ─────────────────────────────────────────────────────────────────────
   Wspólny helper drukowania: otwiera czysty samodzielny dokument HTML
   • Mobile (iOS Safari / Chrome Android) → window.open(Blob URL)
     z auto-print przez inline <script> + widoczny przycisk "Drukuj"
   • Desktop → ukryty iframe + iframe.contentWindow.print()
   ───────────────────────────────────────────────────────────────────── */
const openPrintDocument = (html, alertMsg) => {
  const ua = navigator.userAgent || "";
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);

  if (isMobile) {
    let blobUrl;
    try {
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      blobUrl = URL.createObjectURL(blob);
    } catch (_) {
      alert(alertMsg || "Twoja przeglądarka nie obsługuje pobierania dokumentu. Skontaktuj się telefonicznie: 785 405 785.");
      return;
    }
    const w = window.open(blobUrl, "_blank");
    if (!w) {
      window.location.href = blobUrl;
      return;
    }
    setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }, 60000);
    return;
  }

  // Desktop — ukryty iframe.
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document || iframe.contentDocument;
  if (!doc) { document.body.removeChild(iframe); return; }
  doc.open();
  doc.write(html);
  doc.close();

  const cleanup = () => setTimeout(() => {
    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }, 1000);

  const fire = () => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) {}
    cleanup();
  };

  if (iframe.contentWindow && iframe.contentWindow.document.fonts) {
    iframe.contentWindow.document.fonts.ready.then(() => setTimeout(fire, 250)).catch(() => setTimeout(fire, 600));
  } else {
    setTimeout(fire, 700);
  }
};

/* ─────────────────────────────────────────────────────────────────────
   Wspólny CSS dla wszystkich dokumentów A4 (cennik, wycena).
   Białe tło, czarny tekst, font Bebas Neue + Manrope, pasek narzędzi
   widoczny na ekranie ale ukryty w druku.
   ───────────────────────────────────────────────────────────────────── */
const baseDocCss = `
  @page { size: A4; margin: 10mm 12mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    background: #ffffff !important; color: #000000 !important;
    font-family: 'Manrope', system-ui, -apple-system, sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .toolbar {
    position: sticky; top: 0; z-index: 10;
    display: flex; gap: 8px; align-items: center; justify-content: space-between;
    padding: 12px 16px; background: #0a0a0b; color: #fff;
    font-family: 'Manrope', system-ui, sans-serif; font-size: 14px;
    border-bottom: 2px solid #ea580c;
  }
  .toolbar b { font-family: 'Bebas Neue', Impact, sans-serif; font-size: 18px; letter-spacing: 0.04em; color: #ea580c; }
  .toolbar button {
    background: #ea580c; color: #fff; border: 0; padding: 10px 16px;
    font-family: 'Manrope', system-ui, sans-serif; font-size: 14px; font-weight: 700;
    cursor: pointer; border-radius: 0; letter-spacing: 0.02em; text-transform: uppercase;
  }
  .toolbar button:active { background: #c2410c; }
  @media screen { body { padding: 0 6mm 6mm 6mm; } .page, .quote-page { padding-top: 6mm; } }
  @media print { .toolbar { display: none !important; } body { padding: 0 !important; } }
`;

const autoPrintScript = `
<script>
  (function(){
    function go(){ try { window.focus(); window.print(); } catch(e){} }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function(){ setTimeout(go, 300); }).catch(function(){ setTimeout(go, 800); });
    } else {
      setTimeout(go, 800);
    }
  })();
<\/script>`;

const wrapDocument = (title, css, body) => `<!doctype html>
<html lang="pl">
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Manrope:wght@400;600;700&display=swap" rel="stylesheet">
  <style>${baseDocCss}\n${css}</style>
</head>
<body>
${body}
${autoPrintScript}
</body>
</html>`;

/* ─────────────────────────────────────────────────────────────────────
   printCennik — niezawodny druk PDF cennika (działa też na mobilkach).
   Generuje czysty, samodzielny dokument HTML w ukrytym iframe:
   • zawsze białe tło + czarny tekst (brak dziedziczenia dark mode)
   • twardy page-break — 2 strony A4 (Siatki + Panele)
   • wszystkie style inline → brak konfliktów z aplikacją
   ───────────────────────────────────────────────────────────────────── */
const escapeHtml = (s) => String(s ?? "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const buildCennikPageHtml = (title, dataset, company, today, phonesLine) => {
  const cats = (dataset || []).map((cat) => {
    const rows = (cat.items || []).map((it) => `
      <tr>
        <td class="n">${escapeHtml(it.n)}</td>
        <td class="p">${escapeHtml(it.p)}</td>
      </tr>`).join("");
    return `
      <div class="cat">
        <h2 class="cat-title">${escapeHtml(cat.name)}</h2>
        <table><tbody>${rows}</tbody></table>
      </div>`;
  }).join("");

  return `
    <section class="page">
      <header class="hdr">
        <div class="brand">
          <img class="logo" src="/logo.png" alt="Ogrodzenia Fajsławice" />
          <div>
            <div class="bname">${escapeHtml((company.name || "").toUpperCase())}</div>
            <div class="bsub">Producent siatki ogrodzeniowej · ${escapeHtml(company.address || "")}</div>
          </div>
        </div>
        <div class="meta">
          <div>tel. ${escapeHtml(phonesLine)}</div>
          <div>${escapeHtml(company.email || "")}</div>
          <div>Aktualizacja: ${escapeHtml(today)}</div>
        </div>
      </header>
      <h1 class="title">CENNIK — ${escapeHtml(title)}</h1>
      <div class="grid">${cats}</div>
      <footer class="ftr">
        <div class="ftr-info">Ceny brutto. Mogą ulec zmianie w&nbsp;zależności od&nbsp;ilości zamówienia, koloru i&nbsp;cen surowców. www: ogrodzenia-fajslawice.pl</div>
        <div class="ftr-disclaimer">Niniejszy dokument ma charakter wyłącznie informacyjny i&nbsp;nie stanowi oferty w&nbsp;rozumieniu art. 66 §1 Kodeksu cywilnego.</div>
      </footer>
    </section>`;
};

export const printCennik = (pricing, company) => {
  const today = new Date().toLocaleDateString("pl-PL");
  const phonesLine = (company?.phones || []).map((p) => p.num).join(" · ");

  const css = `
    .page {
      width: 100%;
      page-break-after: always;
      break-after: page;
      color: #1a1a1a;
      font-size: 8pt;
      line-height: 1.3;
      padding: 0;
    }
    .page:last-child { page-break-after: auto; break-after: auto; }
    .hdr {
      display: flex; justify-content: space-between; align-items: flex-start;
      border-bottom: 2px solid #ea580c;
      padding-bottom: 4mm; margin-bottom: 4mm;
    }
    .brand { display: flex; gap: 3mm; align-items: center; }
    .logo { width: 13mm; height: 13mm; object-fit: contain; background: transparent; }
    .bname { font-family: 'Bebas Neue', Impact, sans-serif; font-size: 15pt; letter-spacing: 0.04em; color: #1a1a1a; }
    .bsub { font-size: 7pt; color: #555; margin-top: 1mm; }
    .meta { text-align: right; font-size: 7pt; color: #555; line-height: 1.5; }
    .title {
      font-family: 'Bebas Neue', Impact, sans-serif; font-size: 18pt;
      color: #ea580c; text-transform: uppercase; letter-spacing: 0.04em;
      margin: 0 0 4mm 0;
    }
    .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3mm 5mm; }
    .cat { break-inside: avoid; page-break-inside: avoid; margin-bottom: 1mm; }
    .cat-title {
      font-family: 'Bebas Neue', Impact, sans-serif; font-size: 9.5pt;
      background: #1a1a1a !important; color: #fff !important;
      padding: 1.4mm 2mm; margin: 0 0 0.5mm 0;
      text-transform: uppercase; letter-spacing: 0.03em;
    }
    table { width: 100%; border-collapse: collapse; margin: 0; }
    td { padding: 0.9mm 1.6mm; border-bottom: 0.3pt solid #e5e5e5; font-size: 7.5pt; vertical-align: top; color: #1a1a1a; }
    td.p { text-align: right; white-space: nowrap; font-weight: 700; color: #ea580c !important; }
    .ftr {
      margin-top: 4mm; padding-top: 2mm; border-top: 1px solid #ddd;
      font-size: 6.5pt; color: #777; text-align: center;
    }
    .ftr-info { margin-bottom: 1.5mm; }
    .ftr-disclaimer {
      font-style: italic; font-size: 6pt; color: #999;
      padding: 1.5mm 2mm; background: #fafafa; border-left: 1.5pt solid #ea580c;
      text-align: left;
    }
  `;

  const body = `
<div class="toolbar">
  <b>CENNIK — ${escapeHtml(company?.name || "")}</b>
  <button type="button" onclick="window.print()">Drukuj / Zapisz PDF</button>
</div>
${[
    buildCennikPageHtml("Ogrodzenia z siatki", pricing?.siatka || [], company || {}, today, phonesLine),
    buildCennikPageHtml("Ogrodzenia panelowe", pricing?.panele || [], company || {}, today, phonesLine),
  ].join("")}`;

  const html = wrapDocument(`Cennik — ${escapeHtml(company?.name || "")}`, css, body);
  openPrintDocument(html, "Twoja przeglądarka nie obsługuje pobierania cennika. Skontaktuj się telefonicznie: 785 405 785.");
};

/* ─────────────────────────────────────────────────────────────────────
   printInternalCennik — cennik wewnętrzny (do biura). Duża czcionka,
   każda sekcja na osobnej stronie A4. Sekcje wybierane w adminie.

   sections = tablica ID sekcji (patrz INTERNAL_PRINT_SECTIONS niżej).
   pricing  = { siatka: [...], panele: [...] } jak z /api/site/pricing
   ───────────────────────────────────────────────────────────────────── */
export const INTERNAL_PRINT_SECTIONS = [
  { id: "panele-3d",     label: "Panele 3D (fi4 / fi5 · RAL + OCYNK)",       tab: "panele", match: (n) => /^Panele 3D/i.test(n) },
  { id: "slupki-obejmy", label: "Słupki panelowe (3 grubości) + Obejmy",     tab: "panele", match: (n) => /^Słupki panelowe/i.test(n) || /^Obejmy panelowe/i.test(n) },
  { id: "siatka-ogr",    label: "Siatka ogrodzeniowa (wszystkie wysokości)", tab: "siatka", match: (n) => /^Siatka ogrodzeniowa/i.test(n) },
  { id: "slupki-akces",  label: "Słupki do siatki + Akcesoria + Słupki betonowe", tab: "siatka", match: (n) => /^Słupki do siatki/i.test(n) || /^Akcesoria do siatki/i.test(n) || /^Słupki betonowe/i.test(n) },
  { id: "podmurowki",    label: "Podmurówki betonowe + Łączniki",            tab: "siatka", match: (n) => /^Podmurówki/i.test(n) || /^Łączniki/i.test(n) },
  { id: "siatka-lesna",  label: "Siatka leśna",                              tab: "siatka", match: (n) => /^Siatka leśna/i.test(n) },
];

export const printInternalCennik = (pricing, sectionIds, customPages, company, fontScale = 1.0) => {
  const today = new Date().toLocaleDateString("pl-PL");
  const phonesLine = (company?.phones || []).map((p) => p.num).join(" · ");
  const s = (pt) => (pt * fontScale).toFixed(1);

  const renderPage = (cats) => {
    if (!cats.length) return "";
    const catsHtml = cats.map((cat) => {
      const rows = (cat.items || []).map((it) => `
        <tr>
          <td class="n">${escapeHtml(it.n)}</td>
          <td class="p">${escapeHtml(it.p)}</td>
        </tr>`).join("");
      return `
        <div class="cat">
          <h2 class="cat-title">${escapeHtml(cat.name)}</h2>
          <table><tbody>${rows}</tbody></table>
        </div>`;
    }).join("");

    return `
      <section class="ipage">
        <header class="ihdr">
          <div class="ibrand">
            <img class="ilogo" src="/logo.png" alt="Ogrodzenia Fajsławice" />
            <div>
              <div class="iname">${escapeHtml((company?.name || "").toUpperCase())}</div>
              <div class="isub">Cennik wewnętrzny · ${escapeHtml(today)}</div>
            </div>
          </div>
          <div class="imeta">tel. ${escapeHtml(phonesLine)}</div>
        </header>
        <div class="icats">${catsHtml}</div>
      </section>`;
  };

  const wanted = INTERNAL_PRINT_SECTIONS.filter((s) => (sectionIds || []).includes(s.id));
  const presetPages = wanted.map((section) => {
    const cats = (pricing?.[section.tab] || []).filter((c) => section.match(c.name || ""));
    return renderPage(cats);
  });

  // Custom pages — user picks individual categories by id
  const allCats = [...(pricing?.siatka || []), ...(pricing?.panele || [])];
  const customPagesHtml = (customPages || []).map((cp) => {
    const ids = cp.categoryIds || [];
    // Zachowaj kolejność wybraną przez użytkownika
    const cats = ids.map((id) => allCats.find((c) => c.id === id)).filter(Boolean);
    return renderPage(cats);
  });

  const pages = [...presetPages, ...customPagesHtml].filter(Boolean).join("");

  const css = `
    .ipage {
      width: 100%;
      page-break-after: always;
      break-after: page;
      color: #000;
      font-size: ${s(12)}pt;
      line-height: 1.35;
    }
    .ipage:last-child { page-break-after: auto; break-after: auto; }
    .ihdr {
      display: flex; justify-content: space-between; align-items: center;
      border-bottom: 3px solid #ea580c;
      padding-bottom: 4mm; margin-bottom: 8mm;
    }
    .ibrand { display: flex; gap: 4mm; align-items: center; }
    .ilogo { width: 15mm; height: 15mm; object-fit: contain; }
    .iname { font-family: 'Bebas Neue', Impact, sans-serif; font-size: ${s(20)}pt; letter-spacing: 0.04em; color: #000; }
    .isub { font-size: ${s(9)}pt; color: #555; margin-top: 1mm; }
    .imeta { font-size: ${s(10)}pt; color: #333; font-weight: 700; text-align: right; }
    .icats { display: block; }
    .cat { break-inside: avoid; page-break-inside: avoid; margin-bottom: 6mm; }
    .cat-title {
      font-family: 'Bebas Neue', Impact, sans-serif; font-size: ${s(15)}pt;
      background: #1a1a1a !important; color: #fff !important;
      padding: 2mm 3mm; margin: 0 0 1mm 0;
      text-transform: uppercase; letter-spacing: 0.03em;
    }
    table { width: 100%; border-collapse: collapse; margin: 0; }
    td { padding: 2mm 3mm; border-bottom: 0.5pt solid #ccc; font-size: ${s(12)}pt; vertical-align: top; color: #000; }
    td.n { font-weight: 400; }
    td.p { text-align: right; white-space: nowrap; font-weight: 700; font-size: ${s(13)}pt; color: #ea580c !important; }
    tr:nth-child(even) td { background: #fafafa !important; }
  `;

  const body = `
<div class="toolbar">
  <b>CENNIK WEWNĘTRZNY — ${escapeHtml(company?.name || "")}</b>
  <button type="button" onclick="window.print()">Drukuj / Zapisz PDF</button>
</div>
${pages || '<p style="padding:20mm;font-size:14pt;color:#999">Nie wybrano żadnej sekcji.</p>'}`;

  const html = wrapDocument(`Cennik wewnętrzny — ${escapeHtml(company?.name || "")}`, css, body);
  openPrintDocument(html, "Twoja przeglądarka nie obsługuje pobierania cennika.");
};

/* ─────────────────────────────────────────────────────────────────────
   printQuote — niezawodny druk PDF wyceny z konfiguratora.
   Używa tego samego mechanizmu co printCennik (Blob URL na mobile,
   iframe na desktop). Czyste białe tło, czytelny układ A4.

   payload = {
     title: string,
     date: string (PL format),
     company: { name, address, email, phones },
     config: [{label, value}, ...] — parametry wybrane przez klienta
     items: [{label, qty, unit, unitPrice, total}, ...]
     total: string (sformatowana suma)
     notes: string (opcjonalne uwagi)
   }
   ───────────────────────────────────────────────────────────────────── */
export const printQuote = (payload) => {
  const company = payload.company || {};
  const phonesLine = (company.phones || []).map((p) => p.num).join(" · ");

  const css = `
    .quote-page {
      width: 100%; color: #1a1a1a;
      font-family: 'Manrope', system-ui, sans-serif;
      font-size: 10pt; line-height: 1.45;
    }
    .hdr {
      display: flex; justify-content: space-between; align-items: flex-start;
      border-bottom: 2px solid #ea580c;
      padding-bottom: 5mm; margin-bottom: 6mm;
    }
    .brand { display: flex; gap: 4mm; align-items: center; }
    .logo { width: 16mm; height: 16mm; object-fit: contain; background: transparent; }
    .bname { font-family: 'Bebas Neue', Impact, sans-serif; font-size: 18pt; letter-spacing: 0.04em; color: #1a1a1a; }
    .bsub { font-size: 8pt; color: #555; margin-top: 1mm; }
    .meta { text-align: right; font-size: 8pt; color: #555; line-height: 1.5; }
    .title {
      font-family: 'Bebas Neue', Impact, sans-serif; font-size: 24pt;
      color: #ea580c; text-transform: uppercase; letter-spacing: 0.04em;
      margin: 0 0 5mm 0;
    }
    .config {
      display: grid; grid-template-columns: 1fr 1fr;
      gap: 2mm 8mm;
      background: #f7f7f7;
      padding: 4mm 5mm;
      margin-bottom: 6mm;
      font-size: 9.5pt;
      border-left: 3pt solid #ea580c;
      color: #1a1a1a;
    }
    .config b { color: #1a1a1a; font-weight: 700; }
    table.q {
      width: 100%; border-collapse: collapse; margin-bottom: 4mm;
    }
    table.q th {
      background: #1a1a1a !important; color: #fff !important;
      text-align: left; padding: 2.2mm 3mm;
      font-family: 'Bebas Neue', Impact, sans-serif; font-size: 10pt; letter-spacing: 0.04em;
    }
    table.q th:nth-child(2),
    table.q th:nth-child(3),
    table.q th:nth-child(4),
    table.q td:nth-child(2),
    table.q td:nth-child(3),
    table.q td:nth-child(4) { text-align: right; white-space: nowrap; }
    table.q td {
      padding: 2mm 3mm; border-bottom: 0.4pt solid #ddd;
      font-size: 9.5pt; color: #1a1a1a;
    }
    table.q tfoot td {
      background: #ea580c !important; color: #fff !important;
      font-family: 'Bebas Neue', Impact, sans-serif; font-size: 14pt;
      padding: 3mm; border: none;
    }
    .notes {
      margin: 0 0 6mm 0; padding: 4mm 5mm;
      border-left: 3pt solid #ea580c; background: #fff8f3;
      font-size: 9.5pt; color: #1a1a1a;
    }
    .notes-title {
      font-family: 'Bebas Neue', Impact, sans-serif; font-size: 11pt;
      color: #ea580c; text-transform: uppercase; letter-spacing: 0.04em;
      margin-bottom: 2mm;
    }
    .notes-body { white-space: pre-wrap; color: #1a1a1a; }
    .ftr {
      margin-top: 6mm; padding-top: 3mm; border-top: 1px solid #ddd;
      font-size: 7.5pt; color: #777; text-align: center;
    }
    .ftr-info { margin-bottom: 2mm; }
    .ftr-disclaimer {
      font-style: italic; font-size: 7pt; color: #999;
      padding: 2mm; background: #fafafa; border-left: 1.5pt solid #ea580c;
      text-align: left;
    }
  `;

  const configRows = (payload.config || []).map((c) => `
    <div><b>${escapeHtml(c.label)}:</b> ${escapeHtml(c.value)}</div>`).join("");

  const itemRows = (payload.items || []).map((it) => `
    <tr>
      <td>${escapeHtml(it.label)}</td>
      <td>${escapeHtml(it.qty)} ${escapeHtml(it.unit || "")}</td>
      <td>${escapeHtml(it.unitPrice)}</td>
      <td>${escapeHtml(it.total)}</td>
    </tr>`).join("");

  const notesBlock = (payload.notes && payload.notes.trim())
    ? `<div class="notes"><div class="notes-title">Uwagi do wyceny</div><div class="notes-body">${escapeHtml(payload.notes)}</div></div>`
    : "";

  const body = `
<div class="toolbar">
  <b>WYCENA — ${escapeHtml(company.name || "")}</b>
  <button type="button" onclick="window.print()">Drukuj / Zapisz PDF</button>
</div>
<section class="quote-page">
  <header class="hdr">
    <div class="brand">
      <img class="logo" src="/logo.png" alt="Ogrodzenia Fajsławice" />
      <div>
        <div class="bname">${escapeHtml((company.name || "").toUpperCase())}</div>
        <div class="bsub">Producent siatki ogrodzeniowej · ${escapeHtml(company.address || "")}</div>
      </div>
    </div>
    <div class="meta">
      <div>tel. ${escapeHtml(phonesLine)}</div>
      <div>${escapeHtml(company.email || "")}</div>
      <div>Wycena: ${escapeHtml(payload.date || "")}</div>
    </div>
  </header>

  <h1 class="title">${escapeHtml(payload.title || "Wycena ogrodzenia panelowego")}</h1>

  <div class="config">${configRows}</div>

  <table class="q">
    <thead>
      <tr><th>Pozycja</th><th>Ilość</th><th>Cena jedn.</th><th>Razem</th></tr>
    </thead>
    <tbody>${itemRows}</tbody>
    <tfoot>
      <tr><td colspan="3"><b>SUMA</b></td><td><b>${escapeHtml(payload.total || "")}</b></td></tr>
    </tfoot>
  </table>

  ${notesBlock}

  <footer class="ftr">
    <div class="ftr-info">Wycena orientacyjna. Ceny brutto, mogą ulec zmianie w&nbsp;zależności od&nbsp;ilości, dostępności i&nbsp;cen surowców. Po dokładną wycenę zapraszamy do kontaktu telefonicznego.</div>
    <div class="ftr-disclaimer">Niniejszy dokument ma charakter wyłącznie informacyjny i&nbsp;nie stanowi oferty w&nbsp;rozumieniu art. 66 §1 Kodeksu cywilnego.</div>
  </footer>
</section>`;

  const html = wrapDocument(`Wycena — ${escapeHtml(company.name || "")}`, css, body);
  openPrintDocument(html, "Twoja przeglądarka nie obsługuje pobierania wyceny. Skontaktuj się telefonicznie: 785 405 785.");
};
